import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getBlob, ref as storageRef } from 'firebase/storage';
import { db, functions, storage } from './firebase';
import type {
  AiAnalysis,
  AnalyzeScope,
  GameSettings,
  RankHistoryDoc,
  RankHistoryPoint,
  RunResult,
  SupportedLanguage,
} from '../types';

const GAMES = 'games';

export function subscribeGames(cb: (games: GameSettings[]) => void): () => void {
  const q = query(collection(db, GAMES), orderBy('title'));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<GameSettings, 'id'>) })));
  });
}

export async function getGame(id: string): Promise<GameSettings | null> {
  const ref = doc(db, GAMES, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<GameSettings, 'id'>) };
}

export async function createGame(input: Omit<GameSettings, 'id' | 'createdAt' | 'updatedAt' | 'lastRunAt'>): Promise<string> {
  const ref = await addDoc(collection(db, GAMES), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastRunAt: null,
  });
  return ref.id;
}

export async function updateGame(id: string, patch: Partial<GameSettings>): Promise<void> {
  const { id: _ignored, ...rest } = patch;
  void _ignored;
  await updateDoc(doc(db, GAMES, id), { ...rest, updatedAt: serverTimestamp() });
}

export async function deleteGame(id: string): Promise<void> {
  await deleteDoc(doc(db, GAMES, id));
}

export async function triggerRun(gameId: string): Promise<void> {
  const fn = httpsCallable<{ gameId: string }, { ok: boolean }>(functions, 'triggerGameRun');
  await fn({ gameId });
}

/** Run a free-form AI analysis over the latest keyword data (AI分析 screen). */
export async function analyzeKeywords(
  gameId: string,
  language: SupportedLanguage,
  prompt: string,
  scope: AnalyzeScope
): Promise<{ text: string; analysisId: string }> {
  const fn = httpsCallable<
    { gameId: string; language: SupportedLanguage; prompt: string; scope: AnalyzeScope },
    { text: string; analysisId: string }
  >(functions, 'analyzeKeywords', {
    // The SDK default is 70s — shorter than the function's 300s budget, so
    // long Gemini generations would surface as deadline-exceeded here first.
    timeout: 300_000,
  });
  const res = await fn({ gameId, language, prompt, scope });
  return res.data;
}

/** Live view of the latest analysis logs for a game+language (newest first).
 *  Filtered server-side: each log carries the full resultText, so a
 *  cross-language read would download other languages' results only to
 *  discard them — and let a busy language push this one out of the limit. */
export function subscribeAnalyses(
  gameId: string,
  language: SupportedLanguage,
  cb: (analyses: AiAnalysis[]) => void,
  onError: (err: Error) => void
): () => void {
  const q = query(
    collection(db, GAMES, gameId, 'aiAnalyses'),
    where('language', '==', language),
    orderBy('createdAt', 'desc'),
    limit(30)
  );
  // onError matters here: this subscription is the only channel that shows
  // analysis results AND failures. If the query itself dies (e.g. composite
  // index missing), the page must say so instead of showing an empty history.
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AiAnalysis, 'id'>) })));
    },
    onError
  );
}

/**
 * Read the latest results JSON straight from Cloud Storage using the user's
 * Firebase auth token. Storage rules gate the read on the `admin: true` custom
 * claim, so no server-side signed URL is needed.
 */
export async function fetchLatestResult(gameId: string, language: SupportedLanguage): Promise<RunResult | null> {
  return fetchStorageJson<RunResult>(`results/${gameId}/${language}/latest.json`);
}

/**
 * One keyword's rank time series (≤180 days, oldest first) from the
 * games/{gameId}/rankHistory collection. Looked up by (language, keyword)
 * equality so the client never re-implements the functions-side doc-ID hash.
 * null = no history recorded yet.
 */
export async function fetchKeywordRankHistory(
  gameId: string,
  language: SupportedLanguage,
  keyword: string
): Promise<RankHistoryPoint[] | null> {
  const q = query(
    collection(db, GAMES, gameId, 'rankHistory'),
    where('language', '==', language),
    where('keyword', '==', keyword),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return (snap.docs[0].data() as RankHistoryDoc).points ?? [];
}

async function fetchStorageJson<T>(path: string): Promise<T | null> {
  try {
    const blob = await getBlob(storageRef(storage, path));
    const text = await blob.text();
    return JSON.parse(text) as T;
  } catch (err) {
    if ((err as { code?: string }).code === 'storage/object-not-found') return null;
    throw err;
  }
}
